import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { pretendardFont } from '../../styles/typography';
import campaignData from '../../assets/avocadogiant_korea_campaign_grid_items.json';
import moveArrowIcon from '../../assets/icons/move_arrow.svg';
import {
  PillButton,
  Reveal,
  Section,
  SectionInner,
  SectionLabel,
  SectionTitle,
} from '../section/common';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import {
  openExternalLink,
  pickRandomCampaigns,
  type CampaignItem,
} from '../../utils/listingData';

const VISIBLE_CAMPAIGN_COUNT = 6;

const CampaignWrapper = styled(Section).attrs({ $bg: 'black' as const })`
  padding: 120px 0 160px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: -1px;
    padding: 60px 0 80px;
  }
`;

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 32px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CampaignCard = styled.a<{ $delay: number; $visible: boolean }>`
  ${pretendardFont}
  position: relative;
  display: flex;
  min-height: 420px;
  padding: 32px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  flex: 1 0 0;
  align-self: stretch;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0) 48.4%, rgba(0, 0, 0, 0.6) 100%),
    url(<path-to-image>) lightgray 50% / cover no-repeat,
    #d9d9d9;

  text-decoration: none;
  cursor: pointer;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
    $visible ? 'translateY(0)' : 'translateY(24px)'};
  transition:
    opacity 0.5s ease ${({ $delay }) => $delay}s,
    transform 0.5s ease ${({ $delay }) => $delay}s;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 280px;
    padding: 24px 20px;
    border-radius: 24px;
  }
`;

const CardMedia = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const CardBottomShade = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 48.4%,
    rgba(0, 0, 0, 0.6) 100%
  );
  opacity: 1;
  transition: opacity 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 0;
  }
`;

const CardLimeOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: rgba(211, 238, 52, 0.9);
  opacity: 0;
  transition: opacity 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 1;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
`;

const CardArrowCircle = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: #111111;
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CardArrow = styled.img`
  width: 24px;
  height: 24px;
`;

const CardTitle = styled.div`
  align-self: stretch;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: -0.45px;
  transition: color 0.25s ease;

  ${CampaignCard}:hover & {
    color: #111111;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
  }
`;

const MoreButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 48px;
`;

const CampaignSection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();
  const navigate = useNavigate();
  const allItems = campaignData as CampaignItem[];

  const visibleCampaigns = useMemo(
    () => pickRandomCampaigns(allItems, VISIBLE_CAMPAIGN_COUNT),
    [allItems],
  );

  return (
    <CampaignWrapper id="campaigns">
      <SectionInner ref={ref}>
        <Reveal $visible={isVisible}>
          <HeaderBlock>
            <SectionLabel>CAMPAIGNS</SectionLabel>
            <SectionTitle $light>브랜드를 움직인 이야기들</SectionTitle>
          </HeaderBlock>
        </Reveal>

        <Grid>
          {visibleCampaigns.map((item, i) => (
            <CampaignCard
              key={item.sourceItemId}
              href={item.campaignUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => openExternalLink(event, item.campaignUrl)}
              $delay={i * 0.08}
              $visible={isVisible}
              aria-label={item.imageAlt}
            >
              <CardMedia
                src={item.imageUrl}
                alt={item.imageAlt}
                loading="lazy"
                decoding="async"
              />
              <CardBottomShade />
              <CardLimeOverlay />
              <CardContent>
                <CardArrowCircle aria-hidden>
                  <CardArrow src={moveArrowIcon} alt="" />
                </CardArrowCircle>
                <CardTitle>{item.titleLines.join(' ')}</CardTitle>
              </CardContent>
            </CampaignCard>
          ))}
        </Grid>

        <MoreButtonWrapper>
          <PillButton type="button" onClick={() => navigate('/campaigns')}>
            캠페인 더보기 →
          </PillButton>
        </MoreButtonWrapper>
      </SectionInner>
    </CampaignWrapper>
  );
};

export default CampaignSection;
